import { modalController } from "@ionic/vue";
import router, { route, ROUTE_NAME } from "@/router";
import { t } from "@/plugins/vue-i18n";
import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from "@capacitor/device";
import { ROUTE_NAME_WEB } from "@/router/webRoutes";
import { AnyFn, useThrottleFn } from "@vueuse/core";

import NewsModal from "@/components/modal/NewsModal.vue";

import { useUserStore } from "@/stores/user";
import { useNotificationStore } from "@/stores/notification";
import { useLocationStore } from "@/stores/locations";
import { useSupplierStore } from "@/stores/suppliers";
import { useLoader } from "@/composables/useLoader";

import { useHome } from "@/composables/viewFunctions/useHome";

import { routeEnvironment } from "@/functions/enviroment";
import { popAlertConfirm } from "@/functions/alert";

import { News, Notification } from "@/types/notification";
import { NotificationTopic } from "@/types/enum/notification";
import { Capacitor } from "@capacitor/core";
import DashboardPromotionProduct from "@/components/modal/modalControllerModals/DashboardPromotionProduct.vue";

export function pushNotifications() {
    const userStore = useUserStore()

    async function addFirebaseToken(token: string) {
        //check if token already exists in token array
        const firebaseToken = {
            deviceId: (await Device.getId()).identifier,
            firebaseToken: token
        }

        if (!userStore.sessionInfo) return

        userStore.sessionInfo.firebaseToken = firebaseToken
        await userStore.editUser(userStore.sessionInfo)
    }

    const registerPushNotificationListener = async () => {
        await PushNotifications.addListener('registration',
            (token: any) => {
                if (userStore.sessionInfo.id) {
                    addFirebaseToken(token.value)
                }
            }
        );
    }

    const workSpaceCheck = async (locationId: string, change?: boolean, additionalFn?: AnyFn): Promise<void> => {
        if (change) {
            const { showLoading, hideLoading } = useLoader()
            await showLoading()
            const { setup } = useHome()
            const locationStore = useLocationStore()
            await setup()

            const location = locationStore.locations?.find(location => location.id === locationId)

            if (!location) return;

            locationStore.activeLocation = location

            if (additionalFn) await additionalFn()

            await hideLoading()
        }
    }

    const routeToNotificationOriginSwitch = async (notification: Notification, change?: boolean): Promise<void> => {
        const notificationStore = useNotificationStore()
        const locationId = route?.value?.params?.locationId as string ?? notification.meta?.locationId
        let information: News | undefined;
        const informationId = notification.meta.newsId ?? notification.meta.promotionId ?? notification.meta.productPresentationId ?? notification.meta.informationId

        switch (notification.topic) {
            case NotificationTopic.ORDER:
                if (!notification.meta?.orderId) return
                await workSpaceCheck(notification.meta.locationId, change)

                await router.push({
                    name: routeEnvironment(ROUTE_NAME_WEB.ORDER_DETAIL, ROUTE_NAME.ORDER_DETAILS_HOME),
                    params: {
                        locationId: notification.meta.locationId,
                        orderId: notification.meta.orderId
                    }
                })
                await notificationStore.setRead(notification)
                return;

            case NotificationTopic.PRODUCT_PROMOTION:
            case NotificationTopic.NEWS:
                if (!informationId) return

                information = await notificationStore.getSingleNews(informationId, locationId, notification.topic)

                if (!information) return

                await (await modalController.create({
                    component: NewsModal,
                    componentProps: {
                        information: information,
                        productSuggestion: notification.topic === NotificationTopic.PRODUCT_PROMOTION,
                        noButtons: true
                    }
                })).present()
                await notificationStore.setRead(notification)
                return;

            case NotificationTopic.PROMOTION:
                if (!informationId) return

                information = await notificationStore.getSingleNews(informationId, locationId, notification.topic)

                if (!information) return

                await (await modalController.create({
                    component: DashboardPromotionProduct,
                    componentProps: {
                        information,
                        itemPlaceholder: true,
                        noButtons: true
                    }
                })).present()
                await notificationStore.setRead(notification)
                return;

            case NotificationTopic.REMINDER:
                if (!notification.meta?.listId) return
                await workSpaceCheck(notification.meta.locationId, change, async () => {
                    await useSupplierStore().setSelectedSupplierFromList(notification.meta.listId, notification.meta.locationId)
                })

                await router.push({
                    name: routeEnvironment(ROUTE_NAME_WEB.DASHBOARD_LIST_CONTENT, ROUTE_NAME.DASHBOARD_HOME_LIST),
                    params: {
                        locationId: notification.meta.locationId,
                        listId: notification.meta.listId
                    }
                });
                await notificationStore.setRead(notification)
                return;
            
            case NotificationTopic.MESSAGE:
                console.log('NotificationTopic.MESSAGE', notification)

                await router.push({
                    name: routeEnvironment(ROUTE_NAME_WEB.DASHBOARD_CHAT, ROUTE_NAME.DASHBOARD_CHAT),
                })
                await notificationStore.setRead(notification) 
                return;

            default:
                return;
        }
    }

    const routeToNotificationOrigin = async (notification: Notification, currentLocationId?: string): Promise<void> => {
        if (currentLocationId && notification.meta.locationId && currentLocationId !== notification.meta.locationId) {
            return popAlertConfirm({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                message: t('messages.switchLocation'),
                handler: {
                    ok: () => routeToNotificationOriginSwitch(notification, true)
                }
            })
        }

        await routeToNotificationOriginSwitch(notification)
    }

    const throttleRouteToNotificationOrigin = useThrottleFn(routeToNotificationOrigin, 1000)

    if (Capacitor.isNativePlatform()) PushNotifications.checkPermissions().then(permission => {
        if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') PushNotifications.requestPermissions()
    })

    return {
        routeToNotificationOrigin,
        registerPushNotificationListener,
        throttleRouteToNotificationOrigin,
        routeToNotificationOriginSwitch
    }
}
